import React from 'react';
import { TableCell, Box } from '@mui/material';
import _ from 'lodash';
import {
  prjDeptHeaderTyps,
  prjOrderHeaderTyps,
  prjSalesHeaderTyps,
  prjStatHeaderTyps,
  prjTargetRateHeaderTyps,
  prjExTargetRateHeaderTyps,
} from '../../utils/forecastStaticData';
import { getNumberComma } from '../../utils/numberUtils';

/**
 * @param useKorean: 한글 혼용 여부 스위치 (true: 한글혼용 2줄, false: 일반숫자 1줄)
 */
const OrderStaticTableCell = ({ headerRows, data, isTotalRow, selectPrjTyp, useKorean = true }) => {
  if (!headerRows || !data) return null;

  // 금액 단위 분리 및 한글 포함 로직
  const formatKoreanUnit = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num === 0) return { upper: '0', lower: '' };

    const absNum = Math.abs(Math.floor(num));
    const sign = num < 0 ? '-' : '';

    const eok = Math.floor(absNum / 100000000); 
    const man = Math.floor((absNum % 100000000) / 10000); 
    const rest = absNum % 10000; 

    let upper = '';
    let lower = '';

    if (eok > 0) {
      upper = `${sign}${getNumberComma(eok)}억`;
      const manPart = man > 0 ? `${getNumberComma(man)}만 ` : '';
      const restPart = rest > 0 ? `${getNumberComma(rest)}` : '';
      lower = (manPart + restPart).trim();
    } else if (man > 0) {
      upper = `${sign}${getNumberComma(man)}만`;
      lower = rest > 0 ? getNumberComma(rest) : '';
    } else {
      upper = `${sign}${getNumberComma(rest)}`;
      lower = '';
    }

    return { upper, lower };
  };

  return (
    headerRows &&
    headerRows.map((field, fIndex) => {
      if (field.id?.includes('STAT') && selectPrjTyp !== 'A') return null;

      const headerTyp =
        field?.id === 'STATRATE' ? prjTargetRateHeaderTyps :
        field?.id === 'EXSTATRATE' ? prjExTargetRateHeaderTyps :
        field?.id?.includes('STAT') ? prjStatHeaderTyps : 
        field?.id === 'OA' ? prjOrderHeaderTyps : 
        field?.id === 'HM' ? prjDeptHeaderTyps : 
        prjSalesHeaderTyps;

      return headerTyp.map((type, tIndex) => {
        if (type.id?.includes('STAT') && selectPrjTyp !== 'A') return null;

        const key = `${fIndex}_${tIndex}`;
        let value = _.has(data, `${field.val}.${type.id}`) ? data[field.val][type.id] : '-';
        let fontColor = '#202124'; 
        let fontWeight = isTotalRow ? 600 : 500; 
        let isPercentage = type.id === 'salesProfitMargin' || type.id?.toLowerCase().includes('rate');

        let displayContent;

        if (isPercentage) {
          const numValue = parseFloat(value);
          
          // 1. 여기서 정렬 관련 스타일 변수를 미리 정의합니다.
          let textAlign = 'center'; 

          if (isNaN(numValue) || !isFinite(numValue) || numValue === 0) {
            displayContent = '0 %';
            fontColor = '#70757a'; 
            fontWeight = 400;      
          } else {
            displayContent = `${numValue.toFixed(0)} %`;
            if (numValue >= 70) { fontColor = '#1967d2'; fontWeight = 500; }
            else if (numValue >= 40) { fontColor = '#5e35b1'; fontWeight = 500; }
            else { fontColor = '#d93025'; fontWeight = 500; }
          }
        }
        else {
          if (value === 0 || value === '0' || value === '-') {
            displayContent = value === '-' ? '-' : '0';
            fontColor = (value === 0 || value === '0') ? '#80868b' : '#202124';
          } else {
            // [스위치 로직 적용]
            if (useKorean) {
              const { upper, lower } = formatKoreanUnit(value);
              displayContent = (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: fontWeight, color: field.id === 'ES' ? '#311b92' : fontColor }}>{upper}</span>
                  {lower && (
                    <span style={{ fontSize: '0.75rem', color: '#989999', fontWeight: 500, marginTop: '1px', letterSpacing: '-0.2px' }}>
                      {lower}
                    </span>
                  )}
                </Box>
              );
            } else {
              // 일반 숫자 형태 (한 줄 표시)
              displayContent = getNumberComma(value);
            }
            
            if (field.id === 'ES') {
              fontColor = type.id === 'revenue' ? '#1a237e' : '#311b92'; 
              fontWeight = 600;
            }
          }
        }

        return (
          <TableCell
            key={key}
            align={isPercentage ? "center" : "right"} 
            sx={{
              textAlign: isPercentage ? 'center' : 'right',
              
              width: isPercentage ? (useKorean ? '60px !important' : '60px') : (useKorean ? 'auto' : 'max-content'), 
              minWidth: isPercentage ? (useKorean ? '60px !important' : '60px') : (useKorean ? 'max-content' : '110px'),
              
              padding: isPercentage ? '4px 2px' : '4px 8px',
              whiteSpace: 'nowrap',
              bgcolor: isTotalRow ? (data.id === 'one' ? '#f5f5f5' : '#edf2f7') : 'transparent', 
              color: fontColor,
              fontWeight: fontWeight,
              fontVariantNumeric: 'tabular-nums', 
              borderRight: tIndex === headerTyp.length - 1 
                ? `1px solid ${field.id === 'OA' ? '#d2e3fc' : field.id === 'HM' ? '#f3e5f5' : '#ede7f6'}`
                : '1px solid #f1f3f4',
            }}
          >
            <span style={{ 
              fontSize: '0.8rem', 
              fontWeight: fontWeight,
              display: isPercentage ? 'inline-block' : 'inline',
              width: isPercentage ? '100%' : 'auto'
            }}>
              {displayContent}
            </span>
          </TableCell>
        );
      });
    })
  );
};

export default OrderStaticTableCell;
